import React, { useState, useEffect } from 'react';
import { contractAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { FileText, Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { draft: '#5B616E', sent: '#1E5A8A', signed: '#1F7A5A', expired: '#9A6B1A', terminated: '#B3261E' };
const CONTRACT_TYPES = ['employment', 'freelance', 'vendor', 'nda', 'licensing', 'service'];

export default function AdminContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', type: 'employment', party_name: '', party_email: '', start_date: '', end_date: '', value: '', currency: 'GBP', document_url: '', notes: '', status: 'draft' });
  const [statusFilter, setStatusFilter] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await contractAPI.list(statusFilter ? { status: statusFilter } : {});
      setContracts(r.data.contracts || []);
    } catch { toast.error('Failed to load contracts'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditId(null); setForm({ title: '', type: 'employment', party_name: '', party_email: '', start_date: '', end_date: '', value: '', currency: 'GBP', document_url: '', notes: '', status: 'draft' }); setShowDialog(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ title: c.title, type: c.type, party_name: c.party_name, party_email: c.party_email, start_date: c.start_date || '', end_date: c.end_date || '', value: c.value || '', currency: c.currency || 'GBP', document_url: c.document_url || '', notes: c.notes || '', status: c.status }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.title || !form.party_name) { toast.error('Title and party name required'); return; }
    try {
      if (editId) { await contractAPI.update(editId, form); toast.success('Contract updated'); }
      else { await contractAPI.create(form); toast.success('Contract created'); }
      setShowDialog(false); load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    try { await contractAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const expiringCount = contracts.filter(c => c.end_date && new Date(c.end_date) < new Date(Date.now() + 30 * 86400000) && c.status === 'signed').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-[#B89B5E]" /> Contract Management</h1>
          <p className="text-sm text-[#5B616E] mt-1">Employment, freelance, vendor, and NDA contracts · {contracts.length} total{expiringCount > 0 ? ` · ⚠️ ${expiringCount} expiring within 30 days` : ''}</p>
        </div>
        <Button onClick={openCreate} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Contract</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['', 'draft', 'sent', 'signed', 'expired', 'terminated'].map(s => (
          <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'} className={`text-xs ${statusFilter === s ? 'bg-[#121316] text-white' : ''}`} onClick={() => setStatusFilter(s)}>
            {s || 'All'}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}</div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-12 text-[#5B616E]"><FileText className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>No contracts yet.</p></div>
      ) : (
        <div className="space-y-2">
          {contracts.map(c => (
            <Card key={c.id} className="bg-white border-[#E7E0D6]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-semibold text-sm truncate">{c.title}</h3>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{c.type}</Badge>
                      <Badge style={{ background: `${STATUS_COLORS[c.status] || '#5B616E'}20`, color: STATUS_COLORS[c.status] || '#5B616E' }} className="text-[10px]">{c.status}</Badge>
                    </div>
                    <p className="text-xs text-[#5B616E]">{c.party_name} · {c.party_email}</p>
                    <p className="text-xs text-[#5B616E]">{c.start_date && `Start: ${c.start_date}`} {c.end_date && `· End: ${c.end_date}`} {c.value && `· ${c.currency} ${c.value}`}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    {c.document_url && <a href={c.document_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-7 p-1"><ExternalLink className="w-3.5 h-3.5" /></Button></a>}
                    <Button size="sm" variant="ghost" className="h-7 p-1" onClick={() => openEdit(c)}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 p-1 text-red-500" onClick={() => handleDelete(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Edit Contract' : 'New Contract'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Contract title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{CONTRACT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Party / company name *" value={form.party_name} onChange={e => setForm(f => ({ ...f, party_name: e.target.value }))} />
              <Input type="email" placeholder="Party email" value={form.party_email} onChange={e => setForm(f => ({ ...f, party_email: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#5B616E] block mb-0.5">Start date</label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
              <div><label className="text-xs text-[#5B616E] block mb-0.5">End date</label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Contract value" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
              <Select value={form.currency} onValueChange={v => setForm(f => ({ ...f, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['GBP', 'USD', 'EUR'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input placeholder="Document URL (Google Drive, SharePoint, etc.)" value={form.document_url} onChange={e => setForm(f => ({ ...f, document_url: e.target.value }))} />
            <Input placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editId ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
